<template>
    <div :class="nodeClasses">
        <!-- tree-table-row -->
        <div
            v-if="lazyShow && columns.length > 0"
            :class="rowClasses"
            :style="rowStyle"
            @mouseenter.stop="handleMouseIn"
            @mouseleave.stop="handleMouseOut"
            @click="handleRowClick($event)"
        >
            <!-- tree-table-cell -->
            <template v-for="(col, i) in columns">
                <template v-if="isVisibile(col)">
                    <!-- 带缩进的单元格 -->
                    <tree-table-cell
                        :key="i"
                        :column="col"
                        :data="data"
                        :node="node"
                        :validation="getValidation(col.key)"
                        @click="handleExpand($event)"
                    >
                    </tree-table-cell>
                </template>
                <div
                    v-else
                    :class="[prefixCls + '-cell', 'is-hidden']"
                    :style="{ width: col.width + 'px' }"
                ></div>
            </template>
        </div>
    </div>
</template>
<script>
import CollapseTransition from '../base/collapse-transition'
import { type, getTextWidth } from '../../utils/assist'
import { getParentNodes } from './util'
import Emitter from '../../mixins/emitter'
import TreeTableCell from './tree-table-cell.js'

var cellPadding = 18 // 与css同步

// 行
export default {
    name: 'TreeTableNode',
    mixins: [Emitter],
    components: {
        CollapseTransition,
        TreeTableCell,
    },
    inject: ['treeRoot'],
    provide() {
        return {
            treeNode: this,
            // 编辑节点，供cellEdit组件使用
            editNode: this,
        }
    },
    props: {
        data: {
            type: Object,
            default() {
                return {}
            },
        },
        columns: {
            type: Array,
            default: function () {
                return []
            },
        },
        node: {
            type: Object,
        },
        location: {
            type: String,
            default: 'middle',
            validator: (val) => ['left', 'middle', 'right'].indexOf(val) >= 0,
        },
        validations: {
            type: Object,
            default: () => [],
        },
    },
    data() {
        return {
            dragList: [],
            lazyShow: false, // 懒加载
            prefixCls: 'ivu-tree-table',
        }
    },
    computed: {
        nodeClasses() {
            return [`${this.prefixCls}-node`]
        },
        rowClasses() {
            return [
                this.prefixCls + '-row',
                this.treeRoot.rowClassName,
                {
                    [`is-highlight`]: this.node._isHighlight,
                    'is-hover': this.node._isHover,
                },
            ]
        },
        rowStyle() {
            if (this.node._isHover || this.node._isHighlight) {
                return {}
            }

            var background = this.treeRoot.background,
                color

            if (type(background) == 'array') {
                color = background[this.node.level % background.length]
            }

            if (type(background) == 'function') {
                color = background(this.node)
            }

            return {
                'background-color': color || '#FFFFFF',
            }
        },
    },
    created() {
        // depend track keys
        this.$watch(
            function () {
                this.treeRoot
                    .getTrackKeys(this.data)
                    .forEach((k) => this.data[k])

                return this.data
            },
            function (newVal) {
                // 更新追踪状态
                this.treeRoot.saveTrack(newVal)
            }
        )
        // depend isExpand
        this.$watch(
            function () {
                if (this.treeRoot.isVirtualScroll) {
                    return true
                }

                var isShow = true,
                    pnode = this.node.parentNode

                while (pnode) {
                    isShow = isShow && pnode._isExpand
                    pnode = pnode.parentNode
                }
                return isShow
            },
            function (val) {
                // 首次渲染
                if (this.lazyShow === false) {
                    return (this.lazyShow = val)
                }
                // 二次折叠
                if (this.$el) {
                    this.$el.style.display = val ? 'table' : 'none'
                }
            },
            {
                immediate: true,
            }
        )
        // accUp 末级自动向上汇总
        var accCols = this.columns.filter((v) => v.accUp),
            oldData = Object.assign({}, this.data)

        if (accCols.length && this.node.isEnd) {
            this.$watch(
                function () {
                    return accCols.reduce((acc, col) => {
                        acc[col.key] = this.data[col.key]

                        return acc
                    }, {})
                },
                function (newData) {
                    var hasChange = false,
                        change = accCols.reduce((acc, col) => {
                            var increment =
                                parseFloat(newData[col.key] || 0) -
                                parseFloat(oldData[col.key] || 0)

                            if (!isNaN(increment) && increment != 0) {
                                acc[col.key] = {
                                    increment,
                                    decimalLimit: col.decimalLimit || 2,
                                }
                                hasChange = true
                            }

                            return acc
                        }, {})
                    // 数值向上汇总
                    if (hasChange) {
                        this.treeRoot.accUp(this.node, change)
                        this.treeRoot.$emit(
                            'accUpChange',
                            getParentNodes(this.node)
                        )
                    }
                    // memo
                    oldData = Object.assign({}, newData)
                }
            )
        }
    },
    methods: {
        handleExpand(e) {
            var item = this.data

            if (this.node._isDisabled) return

            // 是否响应折叠
            var target = e.target,
                expander = this.treeRoot.expander,
                childrenKey = this.treeRoot.childrenKey,
                result = false

            // 排除checkbox的单击
            if (target.getAttribute('type') == 'checkbox') {
                return
            }

            if (expander == 'row') {
                result = true
            }

            if (expander == 'cell') {
                result =
                    target == this.$refs.indentCell ||
                    this.$refs.indentCell.contains(target)
            }

            if (expander == 'icon') {
                result = target == this.$refs.arrow.$el
            }

            if (typeof expander == 'function') {
                result = expander(
                    this.$refs.indentCell,
                    this.$refs.arrow.$el,
                    item
                )
            }

            // 不响应Expand
            if (result == false) {
                return
            }

            // async loading
            if (this.treeRoot.loadData && item[childrenKey].length === 0) {
                const treeTable = this.treeRoot

                this.node._isLoading = true
                treeTable.loadData(item, (children) => {
                    // children是异步取到的数据
                    this.node._isLoading = false

                    if (children.length) {
                        // 会触发重新构建，可能存在性能问题todo
                        this.$set(this.data, childrenKey, children)
                        // 为了在异步加载完毕后触发expand
                        this.$nextTick(() => this.handleExpand(e))
                    }
                })

                return
            }

            // 触发expand
            if (item[childrenKey] && item[childrenKey].length) {
                this.node._isExpand = !this.node._isExpand
                this.treeRoot.handleExpand(this.node)
            }
        },
        handleCheck() {
            if (this.node._isDisabled) return

            const changes = {
                _isChecked:
                    !this.node._isChecked && !this.node._isIndeterminate,
                nodeKey: this.node.nodeKey,
            }

            this.treeRoot.handleCheck(changes)
        },
        handleRowClick() {
            this.treeRoot.handleRowClick(this.node)

            // handleSelect
            if (this.node._isDisabled == false) {
                this.treeRoot.handleSelect(this.node.nodeKey)
            }
        },
        handleMouseIn() {
            this.node._isHover = true
        },
        handleMouseOut() {
            this.node._isHover = false
        },
        handleShowPopper(reference, column) {
            var errors,
                content = ''

            errors = this.getValidation(column.key) || {}
            errors = errors.errors || []

            // errorMsg 错误提示
            if (errors.length > 0) {
                content = errors.map((v) => v.message).join(';')
            }
            // tooltip 文本溢出
            else if (column.tooltip) {
                const $content = reference.querySelector(
                    '[data-cls=cell-content]'
                )

                if (
                    $content &&
                    $content.offsetWidth >
                        reference.clientWidth - cellPadding * 2
                ) {
                    content = this.data[column.key]
                }
            }

            if (content.length) {
                this.treeRoot.handleShowPopper({
                    reference,
                    content,
                    width: getTextWidth(content, 12) + 24,
                })
            }
        },
        isVisibile(column) {
            return column.fixed == this.location
        },
        getValidation(key) {
            return this.validations[key]
        },
        // 更新cell数据，供cellEditor组件使用
        editCellChange(key, val) {
            this.data[key] = val
        },
    },
}
</script>
